import { BackIconButton } from '@/components/ui/back-icon-button';
import { DrawerContextBar } from '@/components/ui/drawer-context-bar';
import { Tooltip } from '@/components/ui/tooltip';
import { Box, chakra, HStack, IconButton, Span } from '@chakra-ui/react';
import { MessageText, OpenNewWindow } from 'iconoir-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import type { ContactPlatform } from '../contact-drawer-url-state';
import type { ContactShell } from '../contact-types';
import type { ContactView } from '../contact-views';
import PlatformToggle from './PlatformToggle';

interface ContactContextBarProps {
  actions?: ReactNode;
  contact: ContactShell;
  view: ContactView;
  platform: ContactPlatform;
  onPlatformChange: (platform: ContactPlatform) => void;
  onBack: () => void;
  onClose: () => void;
  /**
   * Force the back affordance even on the profile view. Used by the stacked
   * mobile layout where the detail panel is a pushed second screen that must
   * always be dismissable back to the rail.
   */
  forceBack?: boolean;
  /**
   * Replaces the view title when a sub-level owns the screen (the display-rights
   * composer). The level names itself here rather than stacking a second header
   * with a second back arrow under this one.
   */
  titleOverride?: string | null;
}

/** Resolve the bar title for the active view from the fixture. */
const resolveContextTitle = (
  contact: ContactShell,
  view: ContactView,
  platform: ContactPlatform
) => {
  if (view === 'profile') {
    const profile = contact.profiles.find((p) => p.platform === platform);
    return profile?.contextTitle ?? contact.name;
  }
  const collab = contact.collaborations.find((c) => c.view === view);
  return collab?.title ?? contact.name;
};

/**
 * Sticky bar atop the right detail panel. Holds the back affordance (only when
 * drilled into a collaboration view), the view title, the IG/TikTok toggle and
 * "Voir le profil" (profile view only).
 *
 * The row geometry and the close X come from the shared `DrawerContextBar`,
 * so any other drawer adopting it is aligned with this one by construction.
 */
const ContactContextBar: React.FC<ContactContextBarProps> = ({
  contact,
  actions,
  view,
  platform,
  onPlatformChange,
  onBack,
  onClose,
  forceBack = false,
  titleOverride = null,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isProfile = view === 'profile';
  const showBack = forceBack || !isProfile;
  const title =
    titleOverride ??
    (view === 'outreach'
      ? t('contactDrawer.outreach.title')
      : resolveContextTitle(contact, view, platform));
  const activeProfile = contact.profiles.find((p) => p.platform === platform);
  const availablePlatforms = contact.profiles.map((p) => p.platform);

  return (
    <DrawerContextBar onClose={onClose} closeLabel={t('contactDrawer.actions.close')}>
      <HStack gap="2.5" minW="0">
        {showBack && (
          <BackIconButton aria-label={t('contactDrawer.actions.back')} onClick={onBack} />
        )}
        <Span
          fontFamily="heading"
          fontSize="lg"
          fontWeight="semibold"
          letterSpacing="-0.015em"
          color="dark"
          whiteSpace="nowrap"
          overflow="hidden"
          textOverflow="ellipsis"
        >
          {title}
        </Span>
      </HStack>

      {actions}
      {isProfile && (
        <HStack gap="2" flex="none">
          <PlatformToggle
            value={platform}
            onChange={onPlatformChange}
            availablePlatforms={availablePlatforms}
          />
          {/* DM shortcut: only when viewing the IG profile and an active IG
              thread already exists (cold DMs are forbidden). Deep-links to the
              existing conversation. Mirrors the creators table's DM icon. */}
          {contact.canonicalConversationId && (
            <Tooltip content={t('contactDrawer.actions.goToConversation')}>
              <IconButton
                aria-label={t('contactDrawer.actions.goToConversation')}
                onClick={() => navigate(`/messages/${contact.canonicalConversationId}`)}
                variant="outline"
                size="sm"
                minW="34px"
                boxSize="34px"
                rounded="md"
                flex="none"
                borderColor="border.emphasized"
                bg="panelBg"
                color="primary.500"
                _hover={{ bg: 'whiteSmoke' }}
              >
                <MessageText />
              </IconButton>
            </Tooltip>
          )}
          <chakra.a
            href={activeProfile?.profileUrl}
            target="_blank"
            rel="noopener noreferrer"
            display="flex"
            alignItems="center"
            gap="1.5"
            h="34px"
            px="3"
            rounded="md"
            borderWidth="1px"
            borderColor="border.emphasized"
            bg="panelBg"
            fontFamily="heading"
            fontSize="xs"
            fontWeight="medium"
            color="primary.700"
            _hover={{ bg: 'whiteSmoke' }}
          >
            <Box asChild color="primary.500">
              <OpenNewWindow width={16} height={16} />
            </Box>
            {t('contactDrawer.actions.viewProfile')}
          </chakra.a>
        </HStack>
      )}
    </DrawerContextBar>
  );
};

export default ContactContextBar;
